import type { Coupon, CouponCategorySummary, CouponStats, Product } from "@/store/types";
import { CATEGORIES, COMBO_PROMO_CODE, MBRSHP_CODE, categoryLabel, promoForCategory } from "@/store/constants";

/** Parse a value to an integer, treating null/undefined/NaN as 0. */
export function safeNum(val: unknown): number {
  const n = parseInt(String(val), 10);
  return isNaN(n) ? 0 : n;
}

/** Remaining uses on a coupon. A null/undefined AvailableUses falls back to MaxUses. */
export function safeAvailable(coupon: Coupon): number {
  if (coupon.AvailableUses === null || coupon.AvailableUses === undefined) {
    return safeNum(coupon.MaxUses);
  }
  return safeNum(coupon.AvailableUses);
}

/** Used count on a coupon (null → 0). */
export function safeUsed(coupon: Coupon): number {
  if (coupon.TotalUsed === null || coupon.TotalUsed === undefined) return 0;
  return safeNum(coupon.TotalUsed);
}

/** Coupons sorted by soonest expiry first (does not mutate the input). */
export function getSortedCoupons(coupons: Coupon[]): Coupon[] {
  return coupons
    .slice()
    .sort((a, b) => new Date(a.ExpiryDate ?? 0).getTime() - new Date(b.ExpiryDate ?? 0).getTime());
}

/**
 * Which product category(ies) a coupon can be redeemed against.
 * MBRSHP-coded coupons are for the DE (study guide) category only; all others match their own
 * category (exposed as `Category` or, on older queries, `ProductType`), which may be a single
 * code ("GD") or a comma-separated list of codes ("GD,IMP") for a coupon valid across multiple
 * categories.
 */
export function couponMatchesCategory(coupon: Coupon, category: string): boolean {
  if (coupon.ProductCode === MBRSHP_CODE) return category === "DE";
  return couponCategories(coupon).includes(category);
}

/** Parse a coupon's `Category`/`ProductType` field into its list of category codes. */
function couponCategories(coupon: Coupon): string[] {
  const couponCat = coupon.Category || coupon.ProductType;
  return (couponCat ?? "").split(",").map((c) => c.trim()).filter(Boolean);
}

/** How many categories a coupon covers (1 for a single code, more for a comma-separated list). */
export function couponSpecificity(coupon: Coupon): number {
  if (coupon.ProductCode === MBRSHP_CODE) return 1;
  return new Set(couponCategories(coupon)).size || 1;
}

/**
 * The best coupon with uses left that fits the category: most specific (fewest categories
 * covered) wins, so a single-category coupon is spent before a multi-category one that could
 * also cover the purchase. Among equally-specific coupons, the first in the (caller-sorted,
 * soonest-expiry-first) list wins.
 */
export function getActiveCouponForCategory(coupons: Coupon[], category: string): Coupon | null {
  let best: Coupon | null = null;
  let bestSpecificity = Infinity;
  for (const coupon of coupons) {
    if (safeAvailable(coupon) <= 0 || !couponMatchesCategory(coupon, category)) continue;
    const specificity = couponSpecificity(coupon);
    if (specificity < bestSpecificity) {
      best = coupon;
      bestSpecificity = specificity;
    }
  }
  return best;
}

/**
 * The promotion code to send for a purchase using the given coupon: the dedicated combo promotion
 * when the coupon spans multiple categories, otherwise the product category's own promotion.
 */
export function promoForPurchase(coupon: Coupon, category: string): string | null {
  return couponSpecificity(coupon) > 1 ? COMBO_PROMO_CODE : promoForCategory(category);
}

/**
 * True when the price shown to this viewer (member price for members, standard otherwise,
 * falling back to the other — same rule as PriceTag) is $0. A coupon must never be spent on
 * these. A product with no price at all is NOT treated as free.
 */
export function isFreeForViewer(product: Product, isMember: boolean): boolean {
  const [first, second] = isMember ? [product.Member, product.Standard] : [product.Standard, product.Member];
  const parsed = (v: unknown) => (v === null || v === undefined ? NaN : parseFloat(String(v)));
  const price = Number.isNaN(parsed(first)) ? parsed(second) : parsed(first);
  return price === 0;
}

/** First coupon with uses left (used only for the header expiry warning). */
export function getActiveCoupon(coupons: Coupon[]): Coupon | null {
  for (const coupon of coupons) {
    if (safeAvailable(coupon) > 0) return coupon;
  }
  return null;
}

/** Whole-day count until a coupon expires (negative if already past). */
export function getDaysUntilExpiry(expiryDate: string): number {
  const now = new Date();
  const expiry = new Date(expiryDate);
  return Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

/**
 * The categories the member still has usable coupons for, with remaining uses each.
 * e.g. [{ category: "GD", label: "General Dentistry", count: 10 }, ...].
 */
export function summarizeCouponsByCategory(coupons: Coupon[]): CouponCategorySummary[] {
  const out: CouponCategorySummary[] = [];
  for (const category of CATEGORIES) {
    let count = 0;
    for (const coupon of coupons) {
      const avail = safeAvailable(coupon);
      if (avail > 0 && couponMatchesCategory(coupon, category)) count += avail;
    }
    if (count > 0) out.push({ category, label: categoryLabel(category), count });
  }
  return out;
}

/** Aggregate total / used / remaining across all coupons (remaining clamped at 0). */
export function computeCouponStats(coupons: Coupon[]): CouponStats {
  let total = 0;
  let used = 0;
  let remaining = 0;
  for (const coupon of coupons) {
    total += safeNum(coupon.MaxUses);
    used += safeUsed(coupon);
    remaining += Math.max(0, safeAvailable(coupon));
  }
  return { total, used, remaining };
}
